// Spec section 7. The headline is the product. Most evenings nobody reads
// past it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PACE_STEPS,
  confidenceOf,
  type Profile,
  type WalkSegment,
} from '../../shared/segments';
import { WalkTimeSheet } from './WalkTimeSheet';
import {
  fmtCountdown,
  fmtTime,
  headlinePlan,
  resolveAndRank,
  type HomePayload,
  type ResolvedLeg,
  type ResolvedPlan,
} from './planner';
import { detectPlatform, useAmbientRefresh } from './usePlatform';
import {
  allSegments,
  loadLastPayload,
  loadProfileFreshToday,
  putSegment,
  saveLastPayload,
  saveProfile,
  seedIfEmpty,
} from './store';
import { Avatar, Login } from './Login';
import { PlaceEditor, type GeoResult } from './PlaceEditor';
import { useAuth, type ServerPlace } from './useAuth';
import { useOrigin } from './useOrigin';
import { usePush } from './usePush';

const WORKER =
  import.meta.env.VITE_WORKER_URL ??
  (location.hostname === 'localhost' ? 'http://localhost:8787' : '/api');

declare const __BUILD_STAMP__: string;
const BUILD_STAMP = typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : 'dev';

// Places now come from the signed-in user rather than a constant. These are
// only the fallback for a single-user deployment with no D1 bound.
const FALLBACK_PLACES = [
  { placeKey: 'richmond', label: 'Richmond', address: '48 Sheen Park, TW9 1UW' },
  { placeKey: 'strand40', label: '40 Strand', address: '40 Strand, WC2N 5HZ' },
];

const LEG_ICON: Record<string, string> = {
  walk: '🚶',
  tube: '🚇',
  nationalrail: '🚆',
  overground: '🚈',
  bus: '🚌',
  dlr: '🚝',
};

// Spec 7.2: any leg rendered from cached data carries a visible age.
const STALE_AFTER_MS = 5 * 60_000;

export default function App() {
  const auth = useAuth(WORKER);

  if (auth.state === 'loading') return <main className="login"><p className="hint">One moment…</p></main>;
  if (auth.state === 'anonymous') return <Login onRequest={auth.requestLink} />;

  return <Planner auth={auth} />;
}

function Planner({ auth }: { auth: ReturnType<typeof useAuth> }) {
  const [payload, setPayload] = useState<HomePayload | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [segments, setSegments] = useState<WalkSegment[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [dest, setDest] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [editing, setEditing] = useState<WalkSegment | null>(null);
  const [showPlaces, setShowPlaces] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [editingPlace, setEditingPlace] = useState<ServerPlace | null>(null);
  const [netError, setNetError] = useState<string | null>(null);
  // Spec 5.9: dismissible, and it stays dismissed for that journey. Keyed
  // to the incident so it reappears only when the condition changes state.
  const [dismissedIncident, setDismissedIncident] = useState<string | null>(null);

  const platform = useMemo(detectPlatform, []);

  // A clock that ticks once a second so countdowns move and plans expire,
  // with no network at all. Spec 7.2 and 13.3.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // The start point is "my location" by default, on every platform. Desktop
  // geolocation is coarse (spec 13.1 argued against using it at all), but the
  // accuracy check downgrades a poor fix rather than trusting it, and a stale
  // pin is worse: it is confidently wrong on the day you work somewhere else.
  // The saved default place is the fallback, not the default.
  const defaultPlaceKey = auth.places.find((p) => p.isDefaultDestination)?.placeKey;
  const { origin, error: gpsError, relocate } = useOrigin(pinned, defaultPlaceKey);

  useEffect(() => {
    void (async () => {
      setSegments(await seedIfEmpty());
      setProfile(await loadProfileFreshToday());
      const cached = await loadLastPayload();
      if (cached) {
        setPayload(cached.payload as HomePayload);
        setFetchedAt(cached.at);
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    const q = new URLSearchParams();
    if (pinned) q.set('from', pinned);
    else if (origin && origin.confidence !== 'pinned') {
      q.set('lat', String(origin.lat));
      q.set('lon', String(origin.lon));
      if (origin.accuracyM) q.set('acc', String(Math.round(origin.accuracyM)));
    }
    if (dest) q.set('to', dest);

    try {
      // Must carry the session. Without it the worker cannot see this user's
      // saved places, falls back to the seeded pair, and every setting in the
      // account sheet becomes decorative.
      const res = await auth.authFetch(`/home?${q}`);
      if (!res.ok) throw new Error(`worker returned ${res.status}`);
      const body = (await res.json()) as HomePayload;
      setPayload(body);
      // The service worker sets this when it served us its saved copy
      // because the network was gone. Never claim confidence we do not
      // have: age the payload from when it was generated, not from now.
      const fromCache = res.headers.get('X-From-Cache') === '1';
      setFetchedAt(fromCache ? new Date(body.generatedAt).getTime() : Date.now());
      setNetError(null);
      void saveLastPayload(body);
    } catch (e) {
      // Spec 9.3: never show nothing. Keep rendering the cached payload
      // with a loud stale banner, because a twelve minute old plan is
      // genuinely useful and a spinner is not.
      setNetError(e instanceof Error ? e.message : 'network failed');
    }
  }, [origin, pinned, dest, auth]);

  // Render stale immediately, then update. Do not block first paint.
  useEffect(() => {
    if (!origin && !pinned) return;
    void refresh();
  }, [refresh, origin, pinned]);

  // Refresh on becoming visible, not just on a timer, because browsers
  // throttle background timers hard. Spec 13.3.
  const lastFetchRef = useRef(0);
  useEffect(() => {
    lastFetchRef.current = fetchedAt ?? 0;
  }, [fetchedAt]);

  const { dormant } = useAmbientRefresh(() => void refresh(), fetchedAt);
  const push = usePush(WORKER);

  const segmentMap = useMemo(() => new Map(segments.map((s) => [s.id, s])), [segments]);
  const deskSegment = segmentMap.get('desk>street');

  const myPlaces = auth.places.length ? auth.places : FALLBACK_PLACES;
  // A place with no coordinates cannot be planned to, and the commonest
  // reason to be here on day one is that home has not been set yet.
  const unsetPlace = auth.places.find((p) => p.lat === undefined || p.lat === null);

  // The whole point of putting segments on the device: this recomputes as
  // the clock moves and as the user edits, with no round trip. Spec 8.2.
  const plans: ResolvedPlan[] = useMemo(() => {
    if (!payload || !profile) return [];
    return resolveAndRank(payload, segmentMap, profile, now);
  }, [payload, segmentMap, profile, now]);

  const updateSegment = async (seg: WalkSegment) => {
    await putSegment(seg);
    setSegments(await allSegments());
    setEditing((cur) => (cur && cur.id === seg.id ? seg : cur));
  };

  const cyclePace = async () => {
    if (!profile) return;
    const i = PACE_STEPS.findIndex((p) => p.factor === profile.paceFactor);
    const next = PACE_STEPS[(i + 1) % PACE_STEPS.length]!;
    const updated = { ...profile, paceFactor: next.factor };
    setProfile(updated);
    await saveProfile(updated);
  };

  const paceLabel =
    PACE_STEPS.find((p) => p.factor === profile?.paceFactor)?.label ?? 'Normal';

  const stale = fetchedAt !== null && Date.now() - fetchedAt > STALE_AFTER_MS;
  // Never let a bus plan be the headline while rail is healthy. Spec 5.8.
  const best = headlinePlan(plans);

  const esc = payload?.escalation;
  // Spec 5.9: never fire on the desktop window unless it is focused. A
  // banner on a monitor you are not looking at helps nobody.
  const showEscalation =
    !!esc &&
    esc.state !== 'none' &&
    esc.incidentKey !== dismissedIncident &&
    (platform === 'mobile' || document.visibilityState === 'visible');

  const health = payload?.approachHealth;
  const bothApproachesDown =
    !!health &&
    Object.values(health).length > 0 &&
    Object.values(health).every((s) => s === 'major' || s === 'broken');

  return (
    <main className={platform === 'desktop' ? 'desktop' : 'mobile'}>
      {/* Spec 13.4: a thin bar in the colour of the best available plan, so
          peripheral vision tells you something changed without you reading
          anything. */}
      {platform === 'desktop' && (
        <div className={`ambient-bar ${best?.catchability ?? 'none'} ${esc?.state ?? 'none'}`} />
      )}

      <header>
        <Avatar
          initials={auth.me?.initials ?? '··'}
          title={auth.me?.email ?? 'Account'}
          onClick={() => setShowAccount(true)}
        />
        <div className="controls">
          <button className="chip" onClick={() => setShowPlaces(true)}>
            to {payload?.destination.label ?? '…'} ▾
          </button>
          <button className="chip" onClick={cyclePace} title="How fast are you moving today">
            {paceLabel}
          </button>
        </div>
      </header>

      {(stale || netError) && (
        <div className="banner warn">
          {netError ? 'Cannot reach the network. ' : ''}
          Showing plans from{' '}
          {fetchedAt ? `${Math.round((Date.now() - fetchedAt) / 60_000)} min ago` : 'earlier'}.
        </div>
      )}

      {/* Both approaches degraded at once is the one situation where you
          need to change your plans rather than pick a different train, so
          it replaces the top of the screen. Spec 7. */}
      {bothApproachesDown && (
        <div className="banner bad">
          Both ways into Richmond are disrupted. Rail may not be the answer tonight.
        </div>
      )}

      {payload?.degraded === 'timetabled' && (
        <div className="banner soft">
          Train times are timetabled, not confirmed live.{' '}
          {payload.degradedReason ?? ''}
        </div>
      )}

      {/* Spec 5.9. `blocked` replaces the headline entirely, `strong` sits
          above the plan list, `soft` is one line under the headline. */}
      {showEscalation && esc && (
        <div className={`banner ${esc.state === 'soft' ? 'soft' : 'bad'} escalation`}>
          <div>{esc.message}</div>
          {esc.links && (
            <div className="ride-links">
              {esc.links.map((l) => (
                <a key={l.name} href={l.url} target="_blank" rel="noreferrer">
                  {l.name}
                </a>
              ))}
              {/* No fare quoted. The app has no reliable way to price a
                  ride, and a wrong number is worse than none. Spec 5.9. */}
              <em>Prices from the app, not from us</em>
            </div>
          )}
          <button className="link" onClick={() => setDismissedIncident(esc.incidentKey ?? null)}>
            Dismiss
          </button>
        </div>
      )}

      {dormant && (
        <div className="banner soft">
          Paused after an hour idle. Click anywhere to resume.
        </div>
      )}

      {/* Day one: nothing works properly until the user says where home is. */}
      {unsetPlace && (
        <div className="banner warn">
          Set where {unsetPlace.label.toLowerCase()} is, and the times become yours.{' '}
          <button className="link" onClick={() => setEditingPlace(unsetPlace)}>
            Set it now
          </button>
        </div>
      )}

      {origin?.confidence === 'stale' && (
        <div className="banner warn">
          Using your last known position. <button className="link" onClick={relocate}>Try again</button>
        </div>
      )}

      {gpsError && !origin && (
        <div className="banner warn">
          {gpsError}.{' '}
          <button className="link" onClick={() => setShowPlaces(true)}>
            Set where you are
          </button>
        </div>
      )}

      <h1 className="headline">
        {esc?.state === 'blocked'
          ? esc.message
          : best
            ? `Leave ${best.leaveInMinutes <= 0.5 ? 'now' : `in ${Math.floor(best.leaveInMinutes)} min`}. ${payload?.destination.label} ${fmtTime(best.effectiveArrival)}.`
            : payload
              ? 'Nothing catchable right now.'
              : 'Working out your options'}
      </h1>

      <div className="plans">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} onEditSegment={setEditing} />
        ))}
      </div>

      {/* Spec 13.2: the one genuinely new piece of data on desktop. On
          mobile you are already outside. At a desk you are several floors
          up behind a lift and a reception, and four minutes is often the
          difference between two trains. Surface it prominently, not buried
          in settings. */}
      {platform === 'desktop' && deskSegment && (
        <button className="desk-field" onClick={() => setEditing(deskSegment)}>
          Leaving my desk takes
          <strong>{Math.round((deskSegment.overrideSeconds ?? deskSegment.seedSeconds) / 60)} min</strong>
          <span className={`dot ${confidenceOf(deskSegment)}`} />
        </button>
      )}

      <section className="walk-times">
        <h2>Walk times</h2>
        <p className="hint">
          Tap any walk inside a plan, or a row here. Your numbers always beat ours.
        </p>
        {segments
          .filter((s) => s.pinned)
          .map((s) => (
            <button key={s.id} className="walk-row" onClick={() => setEditing(s)}>
              <span className={`dot ${confidenceOf(s)}`} />
              <span className="walk-label">{s.label}</span>
              <span className="walk-mins">
                {Math.round((s.overrideSeconds ?? s.seedSeconds) / 60)} min
              </span>
            </button>
          ))}
      </section>

      {editing && profile && (
        <WalkTimeSheet
          segment={editing}
          profile={profile}
          onChange={(s) => void updateSegment(s)}
          onClose={() => setEditing(null)}
        />
      )}

      {showPlaces && (
        <div className="sheet-backdrop" onClick={() => setShowPlaces(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h2>Where are you going?</h2>
            {myPlaces.map((p) => (
              <button
                key={p.placeKey}
                className={`place-row ${payload?.destination.id === p.placeKey ? 'current' : ''}`}
                onClick={() => {
                  setDest(p.placeKey);
                  setShowPlaces(false);
                }}
              >
                <span>
                  {p.label}
                  {payload?.destination.id === p.placeKey ? ' ✓' : ''}
                </span>
                <em>{p.address ?? 'Location not set yet'}</em>
              </button>
            ))}
            <h2 className="sub">Starting from</h2>
            {/* My location is the default, so it is listed first. */}
            <button
              className={`place-row ${pinned === null ? 'current' : ''}`}
              onClick={() => {
                setPinned(null);
                setShowPlaces(false);
              }}
            >
              <span>My location{pinned === null ? ' ✓' : ''}</span>
              <em>Works out the journey from where you are standing</em>
            </button>
            {myPlaces.map((p) => (
              <button
                key={p.placeKey}
                className={`place-row ${pinned === p.placeKey ? 'current' : ''}`}
                onClick={() => {
                  setPinned(p.placeKey);
                  setShowPlaces(false);
                }}
              >
                <span>
                  {p.label}
                  {pinned === p.placeKey ? ' ✓' : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Spec 8.5: after an explicit tap, never on load. */}
      {platform === 'mobile' && push.state !== 'unsupported' && (
        <section className="push">
          {push.state === 'granted' ? (
            <button className="link" onClick={() => void push.unsubscribe()}>
              Alerts on. Turn off
            </button>
          ) : !push.installedToHomeScreen ? (
            <p className="hint">
              Add this to your home screen first, then you can turn on disruption alerts. iOS
              refuses them in a browser tab.
            </p>
          ) : push.state === 'denied' ? (
            <p className="hint">Alerts blocked in Settings for this site.</p>
          ) : push.state === 'not-configured' ? (
            <p className="hint">Alerts need VAPID keys on the worker.</p>
          ) : (
            <button className="ghost-inline" onClick={() => void push.subscribe()}>
              Alert me when my usual route breaks
            </button>
          )}
        </section>
      )}

      {showAccount && (
        <div className="sheet-backdrop" onClick={() => setShowAccount(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="account-head">
              <Avatar initials={auth.me?.initials ?? '··'} />
              <div>
                <strong>{auth.me?.email ?? 'Could not load your account'}</strong>
                {auth.me?.isAdmin && <em className="admin-tag">Admin</em>}
                {!auth.me && (
                  <em className="admin-tag">
                    {auth.error ?? 'The server did not answer'}
                  </em>
                )}
              </div>
            </div>

            <h2 className="sub">Where I normally travel</h2>
            <p className="hint">
              These are the two ends of your usual journey. The app works out which way you are
              going from where you are standing.
            </p>
            {auth.places.map((p) => (
              <button key={p.placeKey} className="place-row" onClick={() => setEditingPlace(p)}>
                <span>
                  {p.isHome ? 'Home: ' : 'Work: '}
                  {p.label}
                </span>
                <em>
                  {p.address ??
                    (p.lat !== undefined && p.lat !== null
                      ? `${p.lat.toFixed(4)}, ${p.lon?.toFixed(4)}`
                      : 'Not set yet. Tap to set it')}
                </em>
              </button>
            ))}

            {/* A sheet with nothing in it and no way to add anything is a dead
                end. If the account somehow has no places, offer to create the
                two it needs rather than showing an empty panel. */}
            {auth.places.length === 0 && (
              <>
                <button
                  className="place-row"
                  onClick={() =>
                    setEditingPlace({
                      placeKey: 'home',
                      label: 'Home',
                      isHome: true,
                      isDefaultDestination: false,
                      modelling: 'generic',
                      pinned: true,
                      updatedAt: new Date().toISOString(),
                    })
                  }
                >
                  <span>Home: not set</span>
                  <em>Tap to set where you travel from</em>
                </button>
                <button
                  className="place-row"
                  onClick={() =>
                    setEditingPlace({
                      placeKey: 'work',
                      label: 'Work',
                      isHome: false,
                      isDefaultDestination: true,
                      modelling: 'generic',
                      pinned: true,
                      updatedAt: new Date().toISOString(),
                    })
                  }
                >
                  <span>Work: not set</span>
                  <em>Tap to set where you travel to</em>
                </button>
              </>
            )}

            {auth.me ? (
              <button className="ghost" onClick={auth.signOut}>
                Sign out
              </button>
            ) : (
              <>
                <button className="ghost" onClick={() => void auth.reload()}>
                  Try again
                </button>
                <button className="ghost" onClick={auth.signOut}>
                  Sign in again
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editingPlace && (
        <PlaceEditor
          place={editingPlace}
          currentCoords={
            origin && origin.confidence !== 'pinned'
              ? { lat: origin.lat, lon: origin.lon }
              : null
          }
          onSearch={async (q) => {
            const res = await auth.authFetch(`/geocode?q=${encodeURIComponent(q)}`);
            if (!res.ok) return [];
            const body = (await res.json()) as { results: GeoResult[] };
            return body.results ?? [];
          }}
          onSave={async (p) => {
            await auth.savePlace(p);
            void refresh();
          }}
          onClose={() => setEditingPlace(null)}
        />
      )}

      <footer>
        Powered by TfL Open Data. Powered by the Transport for London Journey Planner API.
        Contains OS data © Crown copyright and database rights. Train data from National Rail.
        <br />
        <span title="If this looks old, this device is running a stale copy">
          build {BUILD_STAMP} · {location.hostname}
        </span>
      </footer>
    </main>
  );
}

/**
 * The step-by-step breakdown, shown when a card is opened.
 *
 * The collapsed chip row answers "roughly how does this go". This answers
 * "what exactly do I board, where, and at what time", which is the question
 * you have while standing on a concourse with two minutes to spare. Anything
 * with a change in it needs this; a single direct train does not, which is
 * why the toggle only appears when there is more than one thing to catch.
 */
function Breakdown({ plan }: { plan: ResolvedPlan }) {
  const steps = plan.legs.filter((l) => l.kind !== 'walk' || l.resolvedMinutes >= 0.5);

  return (
    <ol className="breakdown">
      {steps.map((leg, i) => {
        const mins = Math.max(1, Math.round(leg.resolvedMinutes));

        if (leg.kind === 'walk') {
          const isInterchange = leg.segmentId?.startsWith('ix:');
          const isExit = leg.segmentId?.startsWith('exit:');
          return (
            <li key={i} className="step walk">
              <span className="step-icon">{LEG_ICON.walk}</span>
              <span className="step-body">
                <strong>
                  {isInterchange
                    ? `Change at ${leg.to}`
                    : isExit
                      ? `Walk to ${leg.to}`
                      : `Walk to ${leg.to}`}
                </strong>
                <em>{mins} min{isInterchange ? ' to change platforms' : ''}</em>
              </span>
            </li>
          );
        }

        return (
          <li key={i} className={`step ${leg.severity ? `sev-${leg.severity}` : ''}`}>
            <span className="step-icon">{LEG_ICON[leg.kind] ?? '•'}</span>
            <span className="step-body">
              <strong>
                {leg.dep} {leg.line ?? leg.operator ?? 'Service'}
                {leg.platform ? ` · platform ${leg.platform}` : ''}
              </strong>
              <em>
                {leg.from ? `from ${leg.from}` : ''}
                {leg.arr ? ` · arrives ${leg.to} ${leg.arr}` : ` · ${mins} min`}
              </em>
              {leg.delayMinutes ? (
                <em className="step-late">
                  {leg.delayMinutes} min late{leg.reason ? ` · ${leg.reason}` : ''}
                </em>
              ) : leg.source === 'timetable' ? (
                <em className="step-soft">Timetabled, not confirmed live</em>
              ) : leg.source === 'darwin' ? (
                <em className="step-ok">Confirmed on the live board</em>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function PlanCard({
  plan,
  onEditSegment,
}: {
  plan: ResolvedPlan;
  onEditSegment: (s: WalkSegment) => void;
}) {
  const [open, setOpen] = useState(false);
  // A single direct train needs no breakdown. Anything you have to change
  // between does.
  const transitLegs = plan.legs.filter((l) => l.kind !== 'walk');
  const worthExpanding = transitLegs.length > 1;

  return (
    // Colour by catchability, not by severity. The user does not care that
    // the Bakerloo has minor delays, they care whether they make the 18:47.
    <article className={`card ${plan.catchability}`}>
      <div className="card-top">
        <div className="arrive">
          {/* A bus plan shows a range, because its single number would be a
              median dressed up as a prediction. Spec 5.8. */}
          {plan.effectiveBand
            ? `${fmtTime(plan.effectiveBand.from)} to ${fmtTime(plan.effectiveBand.to)}`
            : fmtTime(plan.effectiveArrival)}
        </div>
        <div className="countdown">
          <span>{fmtCountdown(plan.leaveInMinutes)}</span>
          <em>{plan.catchability === 'tight' ? 'tight' : 'to leave'}</em>
        </div>
      </div>
      <div className="legs">
        {plan.legs.map((leg, i) => (
          <LegChip key={i} leg={leg} onEdit={onEditSegment} />
        ))}
      </div>
      {worthExpanding && (
        <button className="breakdown-toggle" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide the steps' : `Show all ${transitLegs.length} steps`}
        </button>
      )}

      {open && <Breakdown plan={plan} />}

      {/* Reasons that belong to a specific leg are shown on that leg. What
          is left here is anything that applies to the plan as a whole. */}
      {plan.signals
        .filter((s) => !plan.legs.some((l) => l.reason === s.headline))
        .map((s, i) => (
          <div key={i} className={`reason ${s.severity}`}>
            {s.headline}
          </div>
        ))}
    </article>
  );
}

const DELAY_LABEL: Record<string, string> = {
  minor: 'delays',
  major: 'severe delays',
  broken: 'not running',
  unknown: 'no data',
};

function LegChip({ leg, onEdit }: { leg: ResolvedLeg; onEdit: (s: WalkSegment) => void }) {
  const mins = Math.round(leg.resolvedMinutes);
  // Every walking leg is a tappable chip, because the moment you notice a
  // number is wrong is the moment you are looking at it. Spec 7.1.
  if (leg.kind === 'walk' && leg.segment) {
    return (
      <button className={`chip-leg walk ${confidenceOf(leg.segment)}`} onClick={() => onEdit(leg.segment!)}>
        {LEG_ICON.walk} {mins}m
      </button>
    );
  }

  // The delayed leg is marked on the leg itself. A plan-level colour tells
  // you something is wrong somewhere; this tells you which train.
  const sev = leg.severity;
  const title = leg.reason ?? (sev ? DELAY_LABEL[sev] : undefined);

  return (
    <span className={`chip-leg ${sev ? `sev-${sev}` : ''}`} title={title}>
      {LEG_ICON[leg.kind] ?? ''} {leg.line ?? ''} {leg.dep ?? ''}
      {leg.delayMinutes ? <strong> +{leg.delayMinutes}</strong> : null}
      {!leg.delayMinutes && sev && sev !== 'unknown' ? (
        <strong> {DELAY_LABEL[sev]}</strong>
      ) : null}
      {leg.source === 'timetable' ? (
        <em title="Timetabled, not confirmed by live data"> ~</em>
      ) : null}
    </span>
  );
}
